/**
 * Shared login page — ONE implementation for the whole estate.
 *
 * Every app used to hand-roll ~167 near-identical lines of this. The markup
 * was already shared (the .login-* classes in css/layout.css); only the copy
 * differed. That meant a change to how people sign in was a 20-file change,
 * which is exactly why the local username/password boxes sat in front of
 * everyone for as long as they did.
 *
 * 🔴 THE POINT OF THIS COMPONENT
 *
 * Staff were typing their MICROSOFT password into the local form. That form
 * checks credentials against this app's own database, so an Entra password
 * entered there does not work, is not recognised, and has been handed to the
 * wrong authenticator — the exact muscle memory phishing relies on.
 *
 * So: SSO is the only thing on the page. The local form still exists (it is
 * the break-glass path when Entra is unavailable, and some service accounts
 * are local-only) but you have to ASK for it — `?local=1`, or a deliberately
 * understated link at the foot of the card.
 *
 * Usage:
 *   import { renderLoginPage } from '@isg/ui-kit';
 *   export const render = (c) => renderLoginPage(c, {
 *     appName: 'ISG Intranet',
 *     logoLetter: 'I',
 *     features: [t('login.feature_directory'), ...],
 *     t,                       // optional; falls back to English
 *   });
 */
import { api } from '../services/api.js';
import { auth } from '../services/auth.js';
import { t as chromeT } from '../i18n/index.js';

const MS_LOGO = `
  <svg viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
    <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
    <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
    <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
  </svg>`;

const TICK = `
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
    <path d="m9 12 2 2 4-4"/><circle cx="12" cy="12" r="10"/>
  </svg>`;

/* The strings live in ui-kit's CHROME catalogue (`login.*`, `common.sign_in`)
   — en-GB is bundled statically, so an app that never adopted i18n still gets
   correct English, and one that has gets de/cs. They used to be an English
   table in this file, which no translation could reach. */

const escapeHtml = (s) => String(s ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

/**
 * ⚠️ A missing key makes `t()` return THE KEY ITSELF across this estate, so
 * the `t(k) || fallback` idiom never falls back — it renders "login.password"
 * to the user. Compare against the key.
 */
function makeTranslator(appT) {
  return (key) => {
    // An app's own `t` still wins, so an app can reword its sign-in page.
    if (typeof appT === 'function') {
      const v = appT(key);
      if (v && v !== key) return v;
    }
    return chromeT(key);
  };
}

export async function renderLoginPage(container, opts = {}) {
  const {
    appName = 'ISG',
    logoLetter = (appName.trim()[0] || 'I').toUpperCase(),
    features = [],
    t: appT,
    homeUrl = '/',
  } = opts;
  // Shadows nothing imported: the engine's t is imported as chromeT.
  const t = makeTranslator(appT);

  let cfg = { entra_enabled: false, allow_standard_auth: true };
  try {
    cfg = await api.get('/auth/config');
  } catch {
    /* Fall through to the defaults. If /auth/config is unreachable the local
       form is the ONLY way in, so it must not be hidden behind the reveal —
       handled by `ssoAvailable` below. */
  }

  const ssoAvailable = !!cfg.entra_enabled;
  const localAllowed = cfg.allow_standard_auth !== false;

  /* Reveal the local form when the user asked for it (?local=1), or when SSO
     isn't available at all — in that case hiding it would lock everyone out. */
  const params = new URLSearchParams(window.location.search);
  let localVisible = !ssoAvailable || params.get('local') === '1';

  const name = escapeHtml(appName);

  container.innerHTML = `
    <div class="accent-stripe"></div>
    <div class="login-layout">
      <div class="login-hero">
        <div class="login-hero-logo">${escapeHtml(logoLetter)}</div>
        <h1>${name}</h1>
        <div class="login-hero-divider"></div>
        <ul class="login-hero-features">
          ${features.map((f) => `<li>${TICK}${escapeHtml(f)}</li>`).join('')}
        </ul>
      </div>

      <div class="login-form-side">
        <div class="login-card">
          <div class="login-mobile-logo">
            <div class="sidebar-logo-icon" style="margin: 0 auto var(--space-2); width: 3rem; height: 3rem; font-size: 1.125rem;">${escapeHtml(logoLetter)}</div>
            <div style="font-size: 1.125rem; font-weight: 600; color: var(--gray-900);">${name}</div>
          </div>

          <h2>${escapeHtml(t('login.welcome_back'))}</h2>
          <p class="subtitle">${escapeHtml(t('login.sso_hint'))}</p>

          <div class="login-error" id="login-error"></div>

          ${ssoAvailable ? `
            <a href="/api/auth/entra/login" class="btn btn-microsoft btn-full" id="btn-entra">
              ${MS_LOGO}${escapeHtml(t('login.sign_in_microsoft'))}
            </a>
          ` : ''}

          ${localAllowed ? `
            <div id="local-auth" ${localVisible ? '' : 'hidden'}>
              ${ssoAvailable ? `<div class="login-divider">${escapeHtml(t('login.local_heading'))}</div>` : ''}
              ${ssoAvailable ? `<p class="login-local-hint">${escapeHtml(t('login.local_hint'))}</p>` : ''}
              <form id="login-form">
                <div class="form-group">
                  <label class="form-label" for="username">${escapeHtml(t('login.username'))}</label>
                  <input class="form-input" type="text" id="username" name="username"
                         placeholder="${escapeHtml(t('login.username_placeholder'))}" required autocomplete="username" />
                </div>
                <div class="form-group">
                  <label class="form-label" for="password">${escapeHtml(t('login.password'))}</label>
                  <input class="form-input" type="password" id="password" name="password"
                         placeholder="${escapeHtml(t('login.password_placeholder'))}" required autocomplete="current-password" />
                </div>
                <button type="submit" class="btn btn-primary btn-full" id="btn-login">
                  ${escapeHtml(t('common.sign_in'))}
                </button>
              </form>
            </div>

            ${ssoAvailable ? `
              <button type="button" class="login-reveal" id="show-local" ${localVisible ? 'hidden' : ''}>
                ${escapeHtml(t('login.other_options'))}
              </button>` : ''}
          ` : ''}
        </div>
      </div>
    </div>
  `;

  const errorEl = container.querySelector('#login-error');
  const showErr = (msg) => {
    errorEl.textContent = msg;
    errorEl.style.display = 'block';
  };

  /* SSO */
  const ssoBtn = container.querySelector('#btn-entra');
  ssoBtn?.addEventListener('click', (e) => {
    e.preventDefault();
    auth.redirectToSSO();
  });

  /* Reveal the local form. A <button>, not an <a href="?local=1">: reloading
     the page to show a form we already rendered is a worse experience, and a
     real link invites people to bookmark and share the local route. */
  const localWrap = container.querySelector('#local-auth');
  const revealBtn = container.querySelector('#show-local');
  revealBtn?.addEventListener('click', () => {
    localVisible = true;
    localWrap.hidden = false;
    revealBtn.hidden = true;
    container.querySelector('#username')?.focus();
  });

  /* Local sign-in */
  container.querySelector('#login-form')?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const btn = container.querySelector('#btn-login');
    const username = container.querySelector('#username').value.trim();
    const password = container.querySelector('#password').value;

    if (!username || !password) {
      showErr(t('login.required_fields'));
      return;
    }

    btn.disabled = true;
    btn.textContent = t('common.signing_in');
    errorEl.style.display = 'none';

    try {
      await auth.login(username, password);
      window.location.href = homeUrl;
    } catch (err) {
      showErr(err?.message || t('login.login_failed'));
      btn.disabled = false;
      btn.textContent = t('common.sign_in');
    }
  });

  /* Entra OIDC callback — Microsoft redirects back here with ?code&state */
  const code = params.get('code');
  if (code) {
    errorEl.style.display = 'none';
    try {
      const data = await api.post('/auth/entra/callback', {
        code,
        state: params.get('state'),
      });
      if (!data.access_token) throw new Error(t('login.no_token'));
      api.token = data.access_token;
      window.history.replaceState(null, '', homeUrl);
      window.location.href = homeUrl;
    } catch (err) {
      showErr(err?.message || t('login.sso_failed'));
      window.history.replaceState(null, '', '/login');
    }
  }
}
